import { BlockRenderer } from '@/components/BlockRenderer';
import { Breadcrumb } from '@/components/atoms';
import { RelatedPosts, SiloSidebar } from '@/components/molecules';
import { decodeHtmlEntities } from '@/utils/decodeHtmlEntities';
import '@/styles/_article.scss';
import type { AppData } from '@/types/app';

interface SinglePostProps {
  data: AppData;
}

export function SinglePost({ data }: SinglePostProps) {
  const { post, blocks, related_posts, silo } = data;

  const formattedDate = post?.date
    ? new Date(post.date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <article className="single-post">
      <div className="single-post__header">
        {post?.breadcrumbs && post.breadcrumbs.length > 0 && (
          <Breadcrumb items={post.breadcrumbs} />
        )}
        {post?.title && <h1 className="single-post__title">{decodeHtmlEntities(post.title)}</h1>}
        <div className="single-post__meta">
          {formattedDate && (
            <time className="single-post__date" dateTime={post?.date}>
              {formattedDate}
            </time>
          )}
          {formattedDate && post?.author && (
            <span className="single-post__separator">|</span>
          )}
          {post?.author && (
            post.author.link ? (
              <a
                href={post.author.link}
                className="single-post__author"
              >
                {post.author.name}
              </a>
            ) : (
              <span className="single-post__author">
                {post.author.name}
              </span>
            )
          )}
        </div>
        {post?.categories && post.categories.length > 0 && (() => {
          const breadcrumbNames = new Set(
            (post.breadcrumbs ?? []).map((b) => b.name.toLowerCase())
          );
          const filtered = post.categories.filter(
            (cat) => !breadcrumbNames.has(cat.name.toLowerCase())
          );
          return filtered.length > 0 ? (
            <div className="single-post__categories">
              {[...filtered]
                .sort((a, b) => Number(b.is_primary) - Number(a.is_primary))
                .map((cat) => {
                  const label = cat.name.charAt(0).toUpperCase() + cat.name.slice(1);
                  return cat.link ? (
                    <a key={cat.id} href={cat.link} className="single-post__category">
                      {label}
                    </a>
                  ) : (
                    <span key={cat.id} className="single-post__category">
                      {label}
                    </span>
                  );
                })}
            </div>
          ) : null;
        })()}
      </div>

      <div className={`single-post__content${silo ? ' with-silo-sidebar' : ''}`}>
        <div className="single-post__content-wrapper">
            {post?.featured_image && (
              <img
                className="single-post__featured-image"
                src={post.featured_image.url}
                alt={post.featured_image.alt}
                width={356}
                height={237}
              />
            )}
            <BlockRenderer blocks={blocks} />

            {post?.tags && post.tags.length > 0 && (
                  <div className="single-post__footer">
                      <ul className="single-post__categories">
                          {post.tags.map((tag) => (
                              <li key={tag.id} className="single-post__category">
                                  {tag.link ? <a href={tag.link}>{tag.name}</a> : tag.name}
                              </li>
                          ))}
                      </ul>
                  </div>
            )}
        </div>
        {silo && <SiloSidebar silo={silo} />}
      </div>

      <RelatedPosts posts={related_posts ?? []} />
    </article>
  );
}
